import { FunctionComponent, useState } from "react";
import {
    Badge,
    Box,
    Button,
    Divider,
    HStack,
    Icon,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    Tooltip,
    VStack,
    useToast,
} from "@chakra-ui/react";
import { IconDownload, IconStar, IconStarFilled, IconTrash, IconUpload } from "@tabler/icons-react";
import type { AxiosError } from "axios";
import {
    CandidateGridPreset,
    parsePresetData,
    useCreateCandidateGridPreset,
    useDeleteCandidateGridPreset,
    useCandidateGridPresets,
    useUpdateCandidateGridPreset,
    PresetData,
} from "../../api/candidateGridPresets";

export interface CandidatesPresetManagerProps {
    isOpen: boolean;
    onClose: () => void;
    clientId: string;
    onApplyPreset: (presetData: PresetData) => void;
    onSaveCurrentAsPreset: () => void;
}

export const CandidatesPresetManager: FunctionComponent<CandidatesPresetManagerProps> = ({
    isOpen,
    onClose,
    clientId,
    onApplyPreset,
    onSaveCurrentAsPreset,
}) => {
    const toast = useToast();
    const presetsQuery = useCandidateGridPresets(clientId);
    const updatePreset = useUpdateCandidateGridPreset(clientId);
    const deletePreset = useDeleteCandidateGridPreset(clientId);
    const createPreset = useCreateCandidateGridPreset(clientId);

    // When the name collides, we hold the parsed data here and ask the user to rename.
    const [pendingImport, setPendingImport] = useState<{ data: PresetData; name: string } | null>(null);

    const handleApply = (preset: CandidateGridPreset) => {
        const data = parsePresetData(preset.presetJson);
        if (!data) {
            toast({ status: "error", title: "Invalid preset data", isClosable: true });
            return;
        }
        onApplyPreset(data);
        onClose();
    };

    const handleToggleDefault = async (preset: CandidateGridPreset) => {
        await updatePreset.mutateAsync({
            presetId: preset.id,
            payload: {
                name: preset.name,
                presetJson: preset.presetJson,
                isDefault: !preset.isDefault,
            },
        });
    };

    const handleDelete = async (preset: CandidateGridPreset) => {
        await deletePreset.mutateAsync(preset.id);
        toast({ status: "success", title: `Preset "${preset.name}" deleted`, isClosable: true });
    };

    const handleExport = (preset: CandidateGridPreset) => {
        const blob = new Blob([preset.presetJson], { type: "application/json;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${preset.name.replace(/[^a-z0-9]/gi, "_")}.preset.json`);
        link.dispatchEvent(new MouseEvent("click", { bubbles: false, cancelable: true, view: window }));
        URL.revokeObjectURL(url);
    };

    const handleImport = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".json,application/json";
        input.onchange = async (event: Event) => {
            const file = (event.target as HTMLInputElement).files?.[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = async e => {
                const text = e.target?.result as string;
                let parsed: PresetData;
                try {
                    parsed = JSON.parse(text) as PresetData;
                } catch {
                    toast({ status: "error", title: "Invalid JSON file", isClosable: true });
                    return;
                }

                if (!parsed || !Array.isArray(parsed.columnFilters) || !Array.isArray(parsed.sorting)) {
                    toast({ status: "error", title: "File does not contain a valid preset", isClosable: true });
                    return;
                }

                const baseName = file.name.replace(/\.preset\.json$|\.json$/, "");
                // Always prompt for a name, defaulting to the filename
                setPendingImport({ data: parsed, name: baseName });
            };
            reader.readAsText(file);
        };
        input.click();
    };

    const handleConfirmRename = async () => {
        if (!pendingImport) return;
        try {
            await createPreset.mutateAsync({
                name: pendingImport.name,
                presetJson: JSON.stringify(pendingImport.data),
                isDefault: false,
            });
            toast({ status: "success", title: `Preset "${pendingImport.name}" imported`, isClosable: true });
            setPendingImport(null);
        } catch (err) {
            const axiosErr = err as AxiosError<{ errors?: Record<string, string[]> }>;
            const nameErrors = axiosErr?.response?.data?.errors?.["name"];
            if (nameErrors?.some(msg => msg.toLowerCase().includes("already exists"))) {
                toast({
                    status: "warning",
                    title: "Name already taken",
                    description: "You already have a preset with that name. Please choose a different one.",
                    isClosable: true,
                });
            } else {
                toast({
                    status: "error",
                    title: "Import failed",
                    description: nameErrors?.[0] ?? "An unexpected error occurred.",
                    isClosable: true,
                });
                setPendingImport(null);
            }
        }
    };

    const presets = presetsQuery.data ?? [];

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Filter Presets</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <HStack mb={4} justify="space-between">
                        <Button
                            colorScheme="blue"
                            size="sm"
                            onClick={() => {
                                onSaveCurrentAsPreset();
                                onClose();
                            }}
                        >
                            Save Current Filters as Preset
                        </Button>
                        <Tooltip label="Import preset from JSON file">
                            <IconButton
                                aria-label="Import preset"
                                icon={<Icon as={IconUpload} />}
                                size="sm"
                                variant="outline"
                                isLoading={createPreset.isLoading}
                                onClick={handleImport}
                            />
                        </Tooltip>
                    </HStack>

                    <Divider mb={4} />

                    {pendingImport && (
                        <Box mb={4} p={3} borderRadius="md" borderWidth={1} borderColor="blue.200" bg="blue.50">
                            <Text fontSize="sm" fontWeight="medium" mb={2} color="blue.800">
                                Name this preset:
                            </Text>
                            <HStack>
                                <Input
                                    size="sm"
                                    value={pendingImport.name}
                                    maxLength={100}
                                    onChange={e => setPendingImport({ ...pendingImport, name: e.target.value })}
                                    onKeyDown={e => {
                                        if (e.key === "Enter") handleConfirmRename();
                                    }}
                                    autoFocus
                                />
                                <Button
                                    size="sm"
                                    colorScheme="blue"
                                    isDisabled={!pendingImport.name.trim()}
                                    isLoading={createPreset.isLoading}
                                    onClick={handleConfirmRename}
                                >
                                    Import
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setPendingImport(null)}>
                                    Cancel
                                </Button>
                            </HStack>
                        </Box>
                    )}

                    {presetsQuery.isLoading && (
                        <Box textAlign="center" py={4}>
                            <Spinner />
                        </Box>
                    )}

                    {!presetsQuery.isLoading && presets.length === 0 && (
                        <Text color="gray.500" textAlign="center" py={4}>
                            No saved presets. Save your current filters to get started.
                        </Text>
                    )}

                    <VStack align="stretch" spacing={2}>
                        {presets.map(preset => (
                            <HStack
                                key={preset.id}
                                p={3}
                                borderRadius="md"
                                borderWidth={1}
                                borderColor="gray.200"
                                _hover={{ borderColor: "blue.300", bg: "blue.50" }}
                            >
                                <Box flex={1} minWidth={0}>
                                    <HStack>
                                        <Text fontWeight="medium" noOfLines={1}>
                                            {preset.name}
                                        </Text>
                                        {preset.isDefault && (
                                            <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                                Default
                                            </Badge>
                                        )}
                                    </HStack>
                                </Box>

                                <HStack spacing={1} flexShrink={0}>
                                    <Button size="sm" colorScheme="blue" variant="ghost" onClick={() => handleApply(preset)}>
                                        Apply
                                    </Button>

                                    <Tooltip label={preset.isDefault ? "Unset as default" : "Set as default"}>
                                        <IconButton
                                            aria-label={preset.isDefault ? "Unset as default" : "Set as default"}
                                            icon={<Icon as={preset.isDefault ? IconStarFilled : IconStar} />}
                                            size="sm"
                                            variant="ghost"
                                            colorScheme={preset.isDefault ? "yellow" : "gray"}
                                            isLoading={updatePreset.isLoading && updatePreset.variables?.presetId === preset.id}
                                            onClick={() => handleToggleDefault(preset)}
                                        />
                                    </Tooltip>

                                    <Tooltip label="Export preset to JSON">
                                        <IconButton
                                            aria-label="Export preset"
                                            icon={<Icon as={IconDownload} />}
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleExport(preset)}
                                        />
                                    </Tooltip>

                                    <Tooltip label="Delete preset">
                                        <IconButton
                                            aria-label="Delete preset"
                                            icon={<Icon as={IconTrash} />}
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="red"
                                            isLoading={deletePreset.isLoading && deletePreset.variables === preset.id}
                                            onClick={() => handleDelete(preset)}
                                        />
                                    </Tooltip>
                                </HStack>
                            </HStack>
                        ))}
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};
